// userPreferencesApi — the server-sync half of per-user palette persistence.
//
// Contract (server, additive — see spec Open Dependency):
//   GET  /api/me/preferences         -> { palette?: string, ... }
//   PUT  /api/me/preferences         <- { palette: string }   (merge-patch)
//
// Persistence is write-through: localStorage is the always-available cache and
// the server is the cross-machine source of truth WHEN reachable. Because a
// theme is a preference (not a server-owned editable resource), every call here
// is resilient and NON-fatal — a missing endpoint, 404, or network error must
// never blank the app or throw an overlay. It just leaves us on the local
// value. This is the deliberate, documented exception to the "fail loud" rule.

import { apiFetch } from '../apiClient'
import { devLog } from '../devLog'

const PREFS_PATH = '/api/me/preferences'

// Read the stored palette id for the current user, or null if there is none /
// the endpoint is unavailable. Never throws.
export async function fetchRemotePalette() {
  try {
    const prefs = await apiFetch(PREFS_PATH)
    const id = prefs?.palette
    return typeof id === 'string' && id ? id : null
  } catch (e) {
    devLog('[palette] remote preferences read skipped:', e?.status ?? e?.message ?? e)
    return null
  }
}

// Persist the palette id for the current user. Best-effort — resolves whether
// or not the write reached the server; the local cache already holds the value.
export async function saveRemotePalette(id) {
  try {
    await apiFetch(PREFS_PATH, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ palette: id }),
    })
    return true
  } catch (e) {
    devLog('[palette] remote preferences write skipped:', e?.status ?? e?.message ?? e)
    return false
  }
}
